import React from 'react';
import { IStatisticProps, Skeleton, Statistic } from 'ui-lib';
import deviceCss from './devices.css';
import { DeviceDeploymentsTable } from './device-deployment-table';
import { DeviceJobTable } from './device-job-table';
import { DevicePackagesTableComponent } from './device-package-table';
import { FileTransferExecutionsTable } from './file-transfer-history-table';
import { TranferredFilesTable } from './tranferred-files-table';
import { ContainersTable } from './containers-table';
import { DeviceScrInfoComponent } from './scr-info';
import { DeviceCryptoReportComponent } from './crypto-report';
import { DeviceSCRFileListingTable } from './scr-file-listing-table';
import { IDeviceInfoProps } from 'store/devices-store';
import { isString } from 'lodash';
import { RaptorComponentsTable } from './raptor-components-table';
import { RaptorDeploymentsTable } from './raptor-deployments-table';
import { RaptorDeviceLogsComponent } from './raptor-device-log-table';
import { AssureDeviceLogsComponent } from './assure-device-log-table';

export interface ISubCarProps {
    code: string;
    title: React.ReactNode | string;
    contents: React.ReactNode[] | string[];
    loading?: boolean;
    numOfRows?: number;
}

export interface IDeviceTableProps {
    uid: string;
    serialNumber: string;
    accountId: number;
}

export interface IDeviceComponentInfo extends Pick<IDeviceInfoProps, 'deviceName' | 'serialNumber' | 'trampolineVersion'> {
    isOnline: boolean;
    platform: string;
}

export interface IDeviceComponentRenderOptions {
    ref?: any;
    device: IDeviceComponentInfo;
    accountId: number;
}

export const SubCardComponent = (props: ISubCarProps) => {
    const { code: key, title, contents } = props;
    return (
        <div key={key} className={deviceCss['sub-card']}>
            <h3 style={{ width: 'max-content' }}>
                <span className={deviceCss['sub-card-title']}>{title}</span>
            </h3>
            <Skeleton loading={!!props.loading} noOfRows={props.numOfRows}>
                {contents?.map((content, index) =>
                    isString(content) ? (
                        <span key={`span-${key}-${index}`}>{content}</span>
                    ) : (
                        <React.Fragment key={`span-${key}-${index}`}>{content}</React.Fragment>
                    ),
                )}
            </Skeleton>
        </div>
    );
};

export const StatisticItem = (props: IStatisticProps) => {
    return (
        <Statistic
            className={deviceCss['detail-statistic']}
            valueStyle={props.valueStyle}
            title={props.title}
            value={props.value || ' '}
            suffix={props.suffix || ' '}
            groupSeparator=''
            valueRender={props.valueRender}
        />
    );
};
const DEFAULT_COMPONENTS = [
    {
        label: 'Jobs',
        render: (options: IDeviceComponentRenderOptions) => {
            const { accountId, device, ref } = options;
            return (
                <DeviceJobTable
                    accountId={accountId}
                    serialNumber={device.serialNumber}
                    uid={device.deviceName}
                    ref={ref}
                />
            );
        },
    },
    {
        label: 'Packages',
        render: (options: IDeviceComponentRenderOptions) => {
            const { accountId, device } = options;
            return (
                <DevicePackagesTableComponent
                    accountId={accountId}
                    serialNumber={device.serialNumber}
                    uid={device.deviceName}
                />
            );
        },
    },
    {
        label: 'SCR Files',
        render: (options: IDeviceComponentRenderOptions) => {
            const { accountId, device } = options;
            return (
                <DeviceSCRFileListingTable
                    accountId={accountId}
                    serialNumber={device.serialNumber}
                    uid={device.deviceName}
                />
            );
        },
    },
    {
        label: 'Crypto Report',
        render: (options: IDeviceComponentRenderOptions) => {
            const { accountId, device } = options;
            return (
                <DeviceCryptoReportComponent
                    accountId={accountId}
                    serialNumber={device.serialNumber}
                    uid={device.deviceName}
                />
            );
        },
    },
    {
        label: 'Logs',
        render: (options: IDeviceComponentRenderOptions) => {
            const { accountId, device } = options;
            const logsProps = {
                accountId,
                serialNumber: device.serialNumber,
                uid: device.deviceName,
                trampolineVersion: device.trampolineVersion,
                platform: device.platform,
            } as any;

            return device.platform === 'RAPTOR' ? (
                <RaptorDeviceLogsComponent {...logsProps} />
            ) : (
                <AssureDeviceLogsComponent {...logsProps} />
            );
        },
    },
    {
        label: 'SRH',
        render: (options: IDeviceComponentRenderOptions) => {
            const { accountId, device } = options;
            return <DeviceScrInfoComponent accountId={accountId} uid={device.deviceName} />;
        },
    },
    {
        label: 'File Transfer History',
        render: (options: IDeviceComponentRenderOptions) => {
            const { accountId, device } = options;
            return (
                <FileTransferExecutionsTable
                    accountId={accountId}
                    serialNumber={device.serialNumber}
                    uid={device.deviceName}
                />
            );
        },
    },
    {
        label: 'Received Files',
        render: (options: IDeviceComponentRenderOptions) => {
            const { accountId, device } = options;
            return (
                <TranferredFilesTable
                    accountId={accountId}
                    serialNumber={device.serialNumber}
                    uid={device.deviceName}
                />
            );
        },
    },
];
export const DEVICE_COMPONENTS = {
    ASSURE: [
        {
            label: 'Deployments',
            render: (options: IDeviceComponentRenderOptions) => {
                const { accountId, device } = options;
                return (
                    <DeviceDeploymentsTable
                        accountId={accountId}
                        serialNumber={device.serialNumber}
                        uid={device.deviceName}
                    />
                );
            },
        },
        ...DEFAULT_COMPONENTS,
    ],
    RAPTOR: [
        {
            label: 'Deployments',
            render: (options: IDeviceComponentRenderOptions) => {
                const { accountId, device } = options;
                return (
                    <RaptorDeploymentsTable
                        accountId={accountId}
                        serialNumber={device.serialNumber}
                        uid={device.deviceName}
                    />
                );
            },
        },
        {
            label: 'Components',
            render: (options: IDeviceComponentRenderOptions) => {
                const { accountId, device } = options;
                return (
                    <RaptorComponentsTable
                        accountId={accountId}
                        serialNumber={device.serialNumber}
                        uid={device.deviceName}
                    />
                );
            },
        },
        {
            label: 'Containers',
            render: (options: IDeviceComponentRenderOptions) => {
                const { accountId, device } = options;
                return (
                    <ContainersTable
                        accountId={accountId}
                        serialNumber={device.serialNumber}
                        uid={device.deviceName}
                        isOnline={device.isOnline}
                    />
                );
            },
        },
        ...DEFAULT_COMPONENTS,
    ],
};
